import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  ListSubheader,
  MenuItem,
  TextField,
} from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import ToggleRow from "./ToggleRow";
import { DEFAULT_MODEL } from "../../utils/ModelConfig";
import {
  formatModelAgeLabel,
  formatModelReleaseDate,
  getModelCatalogEntry,
  getModelCatalogGroups,
  MODEL_COST_TIER_MAX,
  sortModelsByReleaseDateNewestFirst,
} from "../../utils/modelCatalog";
import "./ModelCardPicker.css";

const ROUNDED_CHIP_SX = {
  height: 22,
  borderRadius: "4px",
  "& .MuiChip-label": {
    px: 0.75,
    lineHeight: 1.2,
  },
  "& .MuiChip-icon": {
    ml: 0.5,
    mr: -0.25,
    color: "inherit",
  },
};

const CAPABILITY_CHIP_SX = {
  ...ROUNDED_CHIP_SX,
  fontSize: "11px",
  backgroundColor: "background.paper",
  border: "1px solid",
  borderColor: "divider",
  color: "text.secondary",
};

const DEFAULT_CHIP_SX = {
  ...ROUNDED_CHIP_SX,
  fontSize: "10px",
  backgroundColor: "primary.main",
  color: "primary.contrastText",
  border: "none",
  "& .MuiChip-label": {
    px: 1,
    lineHeight: 1.2,
  },
};

function ModelCostScale({ tier }) {
  if (!tier || tier < 1) return null;

  const activeTier = Math.min(tier, MODEL_COST_TIER_MAX);
  const label = `${"$".repeat(activeTier)} relative cost`;

  return (
    <span
      className="model-card-picker__cost-scale"
      aria-label={label}
      title={label}
    >
      {Array.from({ length: MODEL_COST_TIER_MAX }, (_, index) => (
        <span
          key={index}
          className={
            index < activeTier
              ? "model-card-picker__cost-scale-dollar model-card-picker__cost-scale-dollar--active"
              : "model-card-picker__cost-scale-dollar"
          }
        >
          $
        </span>
      ))}
    </span>
  );
}

function ModelCapabilityChips({ config }) {
  const chips = [];

  if (config.supportsImages) {
    chips.push({
      key: "vision",
      label: "Vision",
      icon: <ImageOutlinedIcon sx={{ fontSize: 14 }} />,
    });
  }
  if (config.webAccess) {
    chips.push({
      key: "web",
      label: "Web search",
      icon: <LanguageOutlinedIcon sx={{ fontSize: 14 }} />,
    });
  }
  if (config.supportsReasoningEffort) {
    chips.push({
      key: "reasoning",
      label: "Reasoning",
      icon: <PsychologyOutlinedIcon sx={{ fontSize: 14 }} />,
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className="model-card-picker__capabilities">
      {chips.map((chip) => (
        <Chip
          key={chip.key}
          size="small"
          icon={chip.icon}
          label={chip.label}
          sx={CAPABILITY_CHIP_SX}
        />
      ))}
    </div>
  );
}

function ModelMetaSeparator() {
  return <span className="model-card-picker__card-meta-separator">·</span>;
}

const DEFAULT_OPTION_VALUE = "__default__";

function DefaultOptionCard({ label, description, selected = false }) {
  return (
    <div
      className={`model-card-picker__option-card${
        selected ? " model-card-picker__option-card--selected" : ""
      }`}
    >
      <div className="model-card-picker__card-header">
        <div className="model-card-picker__card-title">{label}</div>
      </div>
      {description ? (
        <div className="model-card-picker__card-description">{description}</div>
      ) : null}
    </div>
  );
}

function ModelOptionCard({ model, selected = false }) {
  const releaseLabel = formatModelReleaseDate(model.releasedAt);
  const ageLabel = formatModelAgeLabel(model.releasedAt);

  return (
    <div
      className={`model-card-picker__option-card${
        selected ? " model-card-picker__option-card--selected" : ""
      }`}
    >
      <div className="model-card-picker__card-header">
        <div>
          <div className="model-card-picker__card-title">{model.label}</div>
          <div className="model-card-picker__card-meta">
            {releaseLabel ? <span>Released {releaseLabel}</span> : null}
            {releaseLabel && ageLabel ? (
              <>
                <ModelMetaSeparator />
                <span>{ageLabel}</span>
              </>
            ) : null}
            {(releaseLabel || ageLabel) && model.costTier ? (
              <ModelMetaSeparator />
            ) : null}
            <ModelCostScale tier={model.costTier} />
          </div>
        </div>
        {model.isDefault ? (
          <Chip size="small" label="Default" sx={DEFAULT_CHIP_SX} />
        ) : null}
      </div>

      {model.description ? (
        <div className="model-card-picker__card-description">{model.description}</div>
      ) : null}

      <ModelCapabilityChips config={model} />
    </div>
  );
}

function buildVisibleGroups(value, showLegacy) {
  const baseGroups = getModelCatalogGroups({ includeLegacy: showLegacy });
  if (!value || showLegacy) return baseGroups;

  const selectedEntry = getModelCatalogEntry(value);
  if (!selectedEntry?.legacy) return baseGroups;

  const alreadyVisible = baseGroups.some((group) =>
    group.models.some((model) => model.modelId === value)
  );
  if (alreadyVisible) return baseGroups;

  const providerGroup = baseGroups.find(
    (group) => group.provider === selectedEntry.provider
  );
  if (providerGroup) {
    return baseGroups.map((group) =>
      group.provider === selectedEntry.provider
        ? {
            ...group,
            models: sortModelsByReleaseDateNewestFirst([
              selectedEntry,
              ...group.models,
            ]),
          }
        : group
    );
  }

  return [
    ...baseGroups,
    {
      provider: selectedEntry.provider,
      logoPath: selectedEntry.logoPath,
      models: [selectedEntry],
    },
  ];
}

export default function ModelCardPicker({
  value,
  onChange,
  includeLegacy = false,
  allowDefault = false,
  defaultLabel = "Fast (default)",
  defaultDescription = "Uses a fast, cost-efficient model.",
  className,
  size = "small",
  disabled = false,
  allowEmpty = false,
  placeholder = "Select a model...",
  sx,
  menuProps: menuPropsOverride,
  selectProps: selectPropsOverride,
}) {
  const [showLegacy, setShowLegacy] = useState(includeLegacy);
  const usesDefault = allowDefault && !value;
  const selectedValue = usesDefault
    ? DEFAULT_OPTION_VALUE
    : allowEmpty
      ? value || ""
      : value || DEFAULT_MODEL;
  const selectedModel =
    usesDefault || !selectedValue ? null : getModelCatalogEntry(selectedValue);
  const groups = useMemo(
    () => buildVisibleGroups(selectedValue, showLegacy),
    [selectedValue, showLegacy]
  );
  const hasLegacyModels = useMemo(
    () =>
      getModelCatalogGroups({ includeLegacy: true }).some((group) =>
        group.models.some((model) => model.legacy)
      ),
    []
  );

  const defaultMenuItem = allowDefault ? (
    <MenuItem
      key={DEFAULT_OPTION_VALUE}
      value={DEFAULT_OPTION_VALUE}
      className="model-card-picker__menu-item"
      sx={{
        alignItems: "stretch",
        whiteSpace: "normal",
        py: 1,
        px: 1.25,
        minHeight: "auto",
      }}
    >
      <DefaultOptionCard label={defaultLabel} description={defaultDescription} />
    </MenuItem>
  ) : null;

  const menuItems = groups.flatMap((group) => [
    <ListSubheader
      key={`header-${group.provider}`}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: 1,
        lineHeight: 1.5,
        position: "sticky",
        top: 0,
        zIndex: 2,
        py: 1,
        px: 1.5,
        fontSize: "13px",
        fontWeight: 600,
        color: "text.primary",
        backgroundColor: theme.palette.background.paper,
        backgroundImage: "none",
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow: `0 6px 12px ${theme.palette.background.paper}`,
      })}
    >
      {group.logoPath ? (
        <img
          src={group.logoPath}
          alt=""
          className="model-card-picker__provider-logo"
        />
      ) : null}
      {group.provider}
    </ListSubheader>,
    ...group.models.map((model) => (
      <MenuItem
        key={model.modelId}
        value={model.modelId}
        className="model-card-picker__menu-item"
        sx={{
          alignItems: "stretch",
          whiteSpace: "normal",
          py: 1,
          px: 1.25,
          minHeight: "auto",
        }}
      >
        <ModelOptionCard model={model} />
      </MenuItem>
    )),
  ]);

  return (
    <Box className="model-card-picker">
      <TextField
        select
        className={`model-card-picker__field ${className || ""}`.trim()}
        fullWidth
        size={size}
        disabled={disabled}
        value={selectedValue}
        onChange={(event) => {
          const nextValue = event.target.value;
          if (allowDefault && nextValue === DEFAULT_OPTION_VALUE) {
            onChange(null);
            return;
          }
          onChange(nextValue);
        }}
        sx={{
          "& .MuiInputBase-root": {
            height: "auto",
            alignItems: "flex-start",
          },
          "& .MuiSelect-select": {
            display: "block",
            height: "auto !important",
            minHeight: "unset !important",
            whiteSpace: "normal",
            py: 1,
            px: 1.25,
          },
          "& .MuiSelect-icon": {
            top: 14,
          },
          ...sx,
        }}
        SelectProps={{
          displayEmpty: allowEmpty,
          ...selectPropsOverride,
          renderValue: () => {
            if (usesDefault) {
              return (
                <DefaultOptionCard
                  label={defaultLabel}
                  description={defaultDescription}
                  selected
                />
              );
            }
            if (selectedModel) {
              return <ModelOptionCard model={selectedModel} selected />;
            }
            if (allowEmpty && !selectedValue) {
              return placeholder;
            }
            return selectedValue;
          },
          MenuProps: {
            className: "model-card-picker__menu",
            ...menuPropsOverride,
            PaperProps: {
              className: "model-card-picker__menu-paper",
              ...menuPropsOverride?.PaperProps,
            },
            MenuListProps: {
              className: "model-card-picker__menu-list",
              dense: false,
              ...menuPropsOverride?.MenuListProps,
            },
          },
        }}
      >
        {defaultMenuItem}
        {menuItems}
      </TextField>

      {!includeLegacy && hasLegacyModels ? (
        <div className="model-card-picker__legacy-toggle">
          <ToggleRow
            checked={showLegacy}
            onChange={setShowLegacy}
            title="Show legacy models"
            description="Include older model versions kept for backward compatibility."
          />
        </div>
      ) : null}
    </Box>
  );
}
